import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Loader2, Check, X, Star, Trash2, Sparkles, PackageOpen } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { StarRating } from '../../components/StarRating';
import { UserAvatar } from '../../components/VapeAvatars';

const formatDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('es-MX', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return ''; }
};

const TABS = [
  { id: 'pending', label: 'Pendientes' },
  { id: 'approved', label: 'Publicadas' },
  { id: 'all', label: 'Todas' },
];

const ReviewsSection = () => {
  const [tab, setTab] = useState('pending');
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async (t = tab) => {
    setLoading(true);
    try {
      const q = t === 'all' ? '' : `?status=${t}`;
      const r = await api.get(`/admin/reviews${q}`);
      setReviews(r.data);
    } catch (e) { toast({ title: 'Error', description: 'No se pudieron cargar las reseñas' }); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(tab); }, [tab]);

  const [seeding, setSeeding] = useState(false);
  const seedShowcase = async () => {
    if (!window.confirm('Se reemplazarán las 4 reseñas destacadas (showcase). ¿Continuar?')) return;
    setSeeding(true);
    try {
      const r = await api.post('/admin/seed/reviews');
      toast({ title: 'Reseñas importadas', description: `${r.data.inserted} reseñas destacadas listas.` });
      load(tab);
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'No se pudo importar' });
    } finally {
      setSeeding(false);
    }
  };

  const patch = async (id, upd) => {
    try {
      await api.patch(`/admin/reviews/${id}`, upd);
      load(tab);
      toast({ title: 'Actualizada' });
    } catch { toast({ title: 'Error' }); }
  };

  const remove = async (id) => {
    if (!window.confirm('¿Eliminar reseña?')) return;
    try {
      await api.delete(`/admin/reviews/${id}`);
      load(tab);
      toast({ title: 'Eliminada' });
    } catch { toast({ title: 'Error' }); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-white font-black text-2xl">Reseñas</h1>
          <p className="text-white/60 text-sm">Aprueba, destaca o elimina las reseñas de los clientes.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={seedShowcase}
            disabled={seeding}
            data-testid="admin-seed-reviews"
            className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full bg-transparent border border-orange-500/40 text-orange-300 hover:bg-orange-500/10 text-xs font-bold uppercase tracking-wider transition"
          >
            <PackageOpen className="w-3.5 h-3.5" /> Importar destacadas
          </button>
          <div className="inline-flex bg-neutral-950 border border-white/10 rounded-full p-1">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`reviews-tab-${t.id}`}
              className={`px-4 h-9 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${
                tab === t.id ? 'bg-orange-500 text-black' : 'text-white/60 hover:text-white'
              }`}
            >
              {t.label}
            </button>
          ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-white/70"><Loader2 className="w-5 h-5 animate-spin inline" /> Cargando...</div>
      ) : reviews.length === 0 ? (
        <div className="bg-neutral-950 border border-dashed border-white/15 rounded-2xl p-10 text-center">
          <Star className="w-10 h-10 mx-auto text-orange-400 mb-3" />
          <p className="text-white font-semibold">No hay reseñas en esta vista</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {reviews.map((r) => (
            <div key={r.id} data-testid="admin-review-card" className="bg-neutral-950 border border-white/10 rounded-2xl p-4 md:p-5">
              <div className="flex flex-col md:flex-row md:items-start gap-4">
                <div className="flex items-center gap-3 md:min-w-[220px]">
                  <UserAvatar picture={r.author_picture} name={r.author_name} size={44} />
                  <div className="min-w-0">
                    <p className="text-white font-bold truncate">{r.author_name}</p>
                    <p className="text-[10px] text-white/40 uppercase tracking-wider">{formatDate(r.created_at)}</p>
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <StarRating value={r.rating} size={15} />
                    <span className={`inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full ${
                      r.approved ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30' : 'bg-amber-500/15 text-amber-300 border border-amber-500/30'
                    }`}>
                      {r.approved ? 'Publicada' : 'Pendiente'}
                    </span>
                    {r.featured && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-orange-500 text-black">
                        <Sparkles className="w-3 h-3" /> Destacada
                      </span>
                    )}
                  </div>
                  <h4 className="mt-2 font-bold text-white">{r.title}</h4>
                  <p className="text-sm text-white/70 mt-1">{r.comment}</p>
                </div>

                <div className="flex md:flex-col gap-2 md:min-w-[130px]">
                  {!r.approved ? (
                    <button onClick={() => patch(r.id, { approved: true })} data-testid={`approve-${r.id}`} className="flex-1 h-9 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-black font-bold text-xs uppercase tracking-wider inline-flex items-center justify-center gap-1">
                      <Check className="w-4 h-4" /> Aprobar
                    </button>
                  ) : (
                    <button onClick={() => patch(r.id, { approved: false })} className="flex-1 h-9 rounded-lg bg-white/5 hover:bg-white/10 text-white font-bold text-xs uppercase tracking-wider inline-flex items-center justify-center gap-1 border border-white/10">
                      <X className="w-4 h-4" /> Ocultar
                    </button>
                  )}
                  <button onClick={() => patch(r.id, { featured: !r.featured })} className={`flex-1 h-9 rounded-lg text-xs font-bold uppercase tracking-wider inline-flex items-center justify-center gap-1 border ${
                    r.featured
                      ? 'bg-orange-500/20 text-orange-300 border-orange-500/40 hover:bg-orange-500/30'
                      : 'bg-white/5 text-white/70 border-white/10 hover:bg-white/10'
                  }`}>
                    <Sparkles className="w-4 h-4" /> {r.featured ? 'Quitar' : 'Destacar'}
                  </button>
                  <button onClick={() => remove(r.id)} className="w-9 h-9 rounded-lg bg-white/5 hover:bg-rose-500/15 text-white/60 hover:text-rose-300 flex items-center justify-center border border-white/10">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReviewsSection;
